import React, { useEffect, useState } from "react";
import { Typography, TextField, IconButton, InputAdornment } from "@mui/material";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { alertService } from "client-app-core";

import { Translate, getTranslation } from "orion-components/i18n";

import FilterSettings from "./components/FilterSettings";
import SettingsMenu from "./components/SettingsMenu";
import TimeNotificationGroup from "./components/TimeNotificationGroup";
import * as actions from "./actions";
import { getScrollBarAppState } from "orion-components/AppState/Global/selectors";
import { Search } from "@mui/icons-material";
import { processAggregates } from "../../SharedComponents/trans-utils";
import { matchesSearch, buildDateTimeFormatters } from "../../SharedComponents/NotificationSearchUtils";
import EventBus from "../../SharedComponents/EventBus";
import { isAlertReference } from "../../SharedComponents/AlertReference/utils";

const propTypes = {
	selectFloorPlanOn: PropTypes.func,
	floorPlansWithFacilityFeed: PropTypes.object
};

const defaultProps = {
	selectFloorPlanOn: () => {},
	floorPlansWithFacilityFeed: null
};
// This utility function is used to recursively extract all primitive values
// (string, number, boolean) from a deeply nested object structure.
// It is mainly used to support the search feature by flattening complex
// notification data into a single searchable string.
// This ensures even deeply nested key-value pairs (like metadata fields)
// are included in the final combined search text for accurate filtering.
const collectAllPrimitiveValues = obj => {
	const values = [];

	const traverse = value => {
		if (value === null || value === undefined) return;

		if (["string", "number", "boolean"].includes(typeof value)) {
			values.push(String(value));
			return;
		}

		if (Array.isArray(value)) {
			value.forEach(traverse);
			return;
		}

		if (typeof value === "object") {
			Object.values(value).forEach(traverse);
		}
	};

	traverse(obj);
	return values;
};

const NotificationsTab = props => {
	const {
		notifications,
		componentState,
		readOnly,
		forReplay,
		endDate,
		selectFloorPlanOn,
		floorPlansWithFacilityFeed,
		dir,
		bottomAlertListEnabled
	} = props;

	const { closeBulkNotifications, removeDockedCameraAndState, setFilterSettings } = actions;

	const alertFilterSettings = useSelector(state => state.appState.persisted?.alertFilterSettings);
	const expandedAlert = useSelector(state => state.appState?.dock?.dockData?.expandedAlert);
	const filterGroupBy = alertFilterSettings?.filterGroupBy || "none";
	const userFeeds = useSelector(state => state.session?.userFeeds || {});
	const isReplayDetailOpen = forReplay && Boolean(expandedAlert);

	const activeNotifications = notifications.filter(item => (forReplay ? !item.transactionClosed : !item.closed));

	const [animating, setAnimating] = useState(false);
	const [shouldDisplayLoading, setShouldDisplayLoading] = useState(false);
	const [filterStatus, setFilterStatus] = useState(false);
	const [groupedNotifications, setGroupedNotifications] = useState([]);
	const [groupedItems, setGroupedItems] = useState([]);
	const [filteredActiveNotification, setFilteredActiveNotification] = useState(activeNotifications);
	const [searchString, setSearchString] = useState("");
	const eventTypes = alertFilterSettings?.types ?? [];
	const timeFormatPreference = useSelector(state => state.appState.global.timeFormat);
	// Browser/user timezone
	const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

	// "24-hour" -> true, otherwise false
	const is24Hour = timeFormatPreference === "24-hour";

	// Formatters (recreated only when preference changes)
	const dateOnlyFormatter = React.useMemo(() => {
		return new Intl.DateTimeFormat("en-US", {
			timeZone: userTimeZone,
			dateStyle: "short"
		});
	}, [userTimeZone]);

	const dateTimeFormatter = React.useMemo(() => {
		return new Intl.DateTimeFormat("en-US", {
			timeZone: userTimeZone,
			dateStyle: "short",
			timeStyle: "short",
			// key part:
			hour12: !is24Hour
			// If you prefer explicit 24h cycle instead:
			// hourCycle: is24Hour ? "h23" : "h12"
		});
	}, [userTimeZone, is24Hour]);

	useEffect(() => {
		let tempFilteredActiveNotifications;

		if (!searchString) {
			tempFilteredActiveNotifications = activeNotifications;
		} else {
			const { time24Formatter, hour24Formatter } = buildDateTimeFormatters(userTimeZone);

			tempFilteredActiveNotifications = activeNotifications.filter(notification =>
				matchesSearch({
					searchString,
					notification,
					dateOnlyFormatter,
					dateTimeFormatter,
					time24Formatter,
					hour24Formatter,
					alertFilterSettings,
					userFeeds,
					collectAllPrimitiveValues,
					is24Hour
				})
			);
		}

		const filteredNotifications = eventTypes.length
			? tempFilteredActiveNotifications.filter(notificationItem =>
					eventTypes.some(data => notificationItem?.eventTypeId === data.id && data.filterEnabled)
				)
			: tempFilteredActiveNotifications;

		setFilteredActiveNotification(filteredNotifications);

		if (filterGroupBy !== "none") {
			updateGroupedNotifications(
				filterGroupBy,
				{
					type: groupedItems?.type || [],
					priority: groupedItems?.priority || []
				},
				filteredNotifications
			);
		}
	}, [props, alertFilterSettings, searchString, timeFormatPreference]);

	const updateGroupedNotifications = (groupBy, groupItems, notifications) => {
		if (groupBy == "none") return;
		const notificationsGroupHeaderName = getTranslation("global.dock.notifications.main.notificationGroupHeader");
		// create a copy of lookup list adding in notifications option (which have no type or priority)
		const lookupList = [
			...(groupItems.priority || []),
			...(groupItems.type || []),
			{ name: notificationsGroupHeaderName, id: "default", filterEnabled: true, order: 9999 }
		];
		const lookupProp = groupBy === "priority" ? ["eventPriorityId", "eventTypeId"] : "eventTypeId";

		const groups = {};
		for (const notification of notifications) {
			let propValue;
			if (Array.isArray(lookupProp)) {
				// If notification has a value for any property in the array, use that
				for (const prop of lookupProp) {
					if (notification[prop]) {
						propValue = notification[prop];
						break;
					}
				}
			} else {
				propValue = notification[lookupProp];
			}

			const groupName =
				lookupList.find(listItem => listItem.id === propValue)?.name || notificationsGroupHeaderName;

			if (!groups[groupName]) groups[groupName] = [];
			groups[groupName].push(notification);
		}

		let final = lookupList.flatMap(group => {
			if (!group.filterEnabled || !groups[group.name]) return [];

			// Add group header
			const groupHeader = { isGroupHeader: true, groupName: group.name, groupId: group.id };

			// Filter and sort notifications
			const sortedNotifications = groups[group.name]
				.filter(notification =>
					eventTypes.some(type => type.id === notification.eventTypeId && type.filterEnabled)
				)
				.sort((a, b) => {
					if (a.eventTypeId !== b.eventTypeId) {
						// Sort by eventTypeId order
						const orderA = eventTypes.findIndex(type => type.id === a.eventTypeId);
						const orderB = eventTypes.findIndex(type => type.id === b.eventTypeId);
						return orderA - orderB;
					} else {
						// If eventTypeId order is the same, sort by createdDate (descending)
						return new Date(b.createdDate) - new Date(a.createdDate);
					}
				});

			return [groupHeader, ...sortedNotifications];
		});

		setGroupedNotifications(final);
	};

	const searchNotification = e => {
		setSearchString(e.target.value);
	};

	const searchByReference = () => {
		if (forReplay || !isAlertReference(searchString)) return;
		alertService.getByReference(searchString, false, (err, response) => {
			if (!err && response) EventBus.publish("openAlertProfile", response);
		});
	};

	const scrollBarsEnabled = useSelector(state => getScrollBarAppState(state));
	const processedNotifications = processAggregates(
		filterGroupBy === "none" ? filteredActiveNotification : groupedNotifications
	);

	return (
		<div
			id="notification-tab-wrapper"
			style={{
				...(!forReplay && { overflow: scrollBarsEnabled ? "hidden" : "auto" }),
				...(forReplay && {
					overflow: "hidden",
					height: "100%",
					display: "flex",
					flexDirection: "column",
					minHeight: 0
				})
			}}
			className={"cf" + (componentState.hasError ? " adjusted" : "")}
		>
			{!isReplayDetailOpen && (
				<div
					style={{
						display: "flex",
						flexShrink: 0,
						width: "100%",
						marginBottom: "16px",
						padding: "0 12px",
						alignItems: "center",
						height: 61
					}}
				>
					<div className="search-field" style={{ flex: 1 }} data-testid="orion-components-dock-notifications-notifications-tab-div">
						<TextField
							id="search-field"
							placeholder={getTranslation("global.dock.notifications.main.search")}
							onChange={searchNotification}
							onKeyDown={event => {
								if (event.key === "Enter") searchByReference();
							}}
							variant="standard"
							style={{
								backgroundColor: "transparent",
								width: "100%",
								marginRight: 16
							}}
							sx={{
								"& .MuiInputBase-input": {
									height: "2.15rem !important"
								},
								"& input:-webkit-autofill": {
									boxShadow: "0 0 0 100px #2C2C2C inset",
									WebkitBoxShadow: "0 0 0 100px #2C2C2C inset",
									WebkitTextFillColor: "#FFFFFF"
								}
							}}
							InputProps={{
								startAdornment: (
									<InputAdornment position="start">
										<IconButton onClick={searchByReference} data-testid="orion-components-dock-notifications-notifications-tab-icon-button">
											<Search style={{ color: "6C6C6C", width: 24, height: 24 }} />
										</IconButton>
									</InputAdornment>
								)
							}} data-testid="orion-components-dock-notifications-notifications-tab-text-field"
						/>
					</div>
					<FilterSettings
						updateGroupedNotifications={updateGroupedNotifications}
						setGroupedItems={setGroupedItems}
						filteredActiveNotification={filteredActiveNotification}
					/>
					{!readOnly && (
						<SettingsMenu
							notifications={notifications}
							forReplay={forReplay}
							setAnimating={setAnimating}
							bottomAlertListEnabled={bottomAlertListEnabled}
							setShouldDisplayLoading={setShouldDisplayLoading}
						/>
					)}
				</div>
			)}
			{!isReplayDetailOpen && shouldDisplayLoading && (
				<div>
					<Typography
						style={{
							padding: "24px 0px",
							color: "#B5B9BE",
							display: "inherit"
						}}
						align="center"
						variant="caption"
					>
						{componentState.hasError ? (
							<Translate value="global.dock.notifications.main.checkNetConn" style={{ width: "100%" }} />
						) : (
							<Translate value="global.dock.notifications.main.loading" style={{ width: "100%" }} />
						)}
					</Typography>
				</div>
			)}

			<div
				style={{
					flex: 1,
					minHeight: 0,
					overflow: isReplayDetailOpen ? "hidden" : scrollBarsEnabled ? "hidden" : "auto"
				}}
			>
				{processedNotifications.length === 0 ? (
					<Typography
						style={{
							padding: "24px 0px",
							color: "#B5B9BE",
							display: "inherit",
							border: "1px solid #707070",
							borderRadius: "10px",
							height: "143px",
							margin: "23px 48px",
							display: "flex",
							alignItems: "center"
						}}
						align="center"
						variant="caption"
						sx={{ color: "#98999D" }}
					>
						<Translate
							value="global.dock.notifications.main.noAlarmsOrNotifications"
							style={{ width: "100%", fontSize: 14, color: "#B4B8BC" }}
						/>
					</Typography>
				) : (
					<></>
				)}
				{processedNotifications.length > 0 && (
					<div
						style={{
							padding: isReplayDetailOpen ? 0 : "0 13px",
							height: "100%",
							minHeight: 0,
							display: "flex",
							flex: 1,
							overflow: "hidden"
						}}
					>
						<TimeNotificationGroup
							dir={dir}
							archive={false}
							readOnly={readOnly}
							forReplay={forReplay}
							notifications={processedNotifications}
							animating={animating}
							bulkAction={closeBulkNotifications}
							componentState={componentState}
							removeDockedCameraAndState={removeDockedCameraAndState}
							endDate={endDate}
							selectFloorPlanOn={selectFloorPlanOn}
							floorPlansWithFacilityFeed={floorPlansWithFacilityFeed}
							groupedNotification={filterGroupBy != "none"}
							filterStatus={filterStatus}
						/>
					</div>
				)}
			</div>
		</div>
	);
};

NotificationsTab.propTypes = propTypes;
NotificationsTab.defaultProps = defaultProps;

export default NotificationsTab;
